import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Grid } from 'antd';
import './HomePage.css';


import authorPortrait from '../../assets/images/author_portrait.webp';
import pic4 from '../../assets/images/pic-4.webp';

import { ArticleService, CategoryService, WPPost, WPCategory } from '../../services/api.services';
import config from '../../config/config';

const authorAvatar = authorPortrait;
const cookbookBanner = pic4;

/* ─── Component ─── */
const HomePage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const [categories, setCategories] = useState<Array<WPCategory & { img: string }>>([]);
  const [loading, setLoading] = useState(true);

  const [heroSlides, setHeroSlides] = useState<WPPost[]>([]);
  const [popularRecipesData, setPopularRecipesData] = useState<WPPost[]>([]);
  const [latestRecipes, setLatestRecipes] = useState<WPPost[]>([]);
  const [vegRecipes, setVegRecipes] = useState<WPPost[]>([]);
  const [nonVegRecipes, setNonVegRecipes] = useState<WPPost[]>([]);
  const [featuredRecipes, setFeaturedRecipes] = useState<WPPost[]>([]);
  const [seasonCards, setSeasonCards] = useState<WPPost[]>([]);
  const [makeToday, setMakeToday] = useState<WPPost | null>(null);

  useEffect(() => {
    Promise.all([
      ArticleService.getHomePageData(),
      CategoryService.getAll()
    ]).then(([homeRes, catsRes]) => {
      const hd = homeRes.data;
      setHeroSlides(hd.hero || []);
      setPopularRecipesData(hd.popular || []);
      setLatestRecipes(hd.latest || []);
      setVegRecipes(hd.veg || []);
      setNonVegRecipes(hd.non_veg || []);
      setSeasonCards(hd.featured || []);
      setFeaturedRecipes((hd.featured || []).slice(0, 3));
      setMakeToday(hd.make_today || null);

      // Only show categories that have an image uploaded from the backend
      const enrichedCats = catsRes.data
        .filter((c: any) => c.img)
        .map((c: any) => c);
      setCategories(enrichedCats);
      setLoading(false);
    }).catch(e => {
      console.error('Failed to fetch WordPress data:', e);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (heroSlides.length === 0) return;
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 8000);
    return () => clearTimeout(timer);
  }, [heroSlides.length, currentSlide]);

  const goToSlide = (dir: number) => {
    if (heroSlides.length === 0) return;
    setCurrentSlide((prev) => (prev + dir + heroSlides.length) % heroSlides.length);
  };

  if (loading) {
    return (
      <main className="homepage" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
         <div style={{ width: 52, height: 52, border: '3px solid #eee', borderTop: '3px solid #306046', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
         <p style={{ marginTop: 20, color: '#888', fontFamily: 'Playfair Display, serif' }}>Loading WordPress data…</p>
         <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </main>
    );
  }

  if (!loading && heroSlides.length === 0) {
    return (
      <main className="homepage" style={{ textAlign: 'center', padding: '100px 0' }}>
        <h2>No recipes found.</h2>
        <p>Please ensure WordPress is running and has published posts.</p>
      </main>
    );
  }

  return (
    <main className="homepage">

      {/* ── HERO SLIDER ── */}
      <section className="hero-slider">
        <div className="hero-track-wrapper">
          <div className="hero-track" style={{
            transform: !screens.md ? 'none' : `translateX(-${currentSlide * 100}%)`,
            display: 'flex',
            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            width: '100%'
          }}>
            {heroSlides.map((slide, i) => (
              <Link
                to={`/blog-details/${slide.slug || slide.id}`}
                key={slide.id}
                className={`hero-slide ${i === currentSlide ? 'active' : ''}`}
                style={{ backgroundImage: `url("${slide.image || ''}")`, flex: '0 0 100%', width: '100%', textDecoration: 'none', color: 'inherit' }}
              >
                <div className="hero-overlay" />
                <div className="hero-content">
                  <h1 className="hero-title">{slide.title}</h1>
                  {slide.category && slide.category.toUpperCase() !== 'UNCATEGORIZED' && (
                    <p className="hero-category">{slide.category}</p>
                  )}
                  <div className="hero-meta">
                    <span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      {slide.views > 1000 ? (slide.views / 1000).toFixed(1) + 'K' : slide.views}
                    </span>
                    <span>
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
                      {slide.comments}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Arrows - Desktop Only */}
        {screens.md && heroSlides.length > 1 && (
          <>
            <button className="slider-arrow left" onClick={() => goToSlide(-1)} aria-label="Previous slide">&#8249;</button>
            <button className="slider-arrow right" onClick={() => goToSlide(1)} aria-label="Next slide">&#8250;</button>
          </>
        )}

        {/* Thumbnail Strip - Desktop Only */}
        {screens.md && heroSlides.length > 1 && (
          <div className="thumbnail-strip">
            {heroSlides.map((slide, i) => (
              <div
                className={`thumb-item ${i === currentSlide ? 'active' : ''}`}
                key={slide.id}
                onClick={() => setCurrentSlide(i)}
              >
                <img src={slide.image || ''} alt={slide.title} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── MOST POPULAR RECIPES ── */}
      <section className="popular-section">
        <h2 className="section-title">Most Popular Recipes</h2>
        <div className="popular-grid">
          {popularRecipesData.map((item, i) => (
            <Link to={`/blog-details/${item.slug || item.id}`} className={`popular-card ${i === 4 ? 'green-bg' : ''}`} key={item.id} style={{ textDecoration: 'none' }}>
              <img src={item.image || ''} alt={item.title} />
              <div className="popular-card-overlay" />

              {item.date && (
                <div className="popular-badge">{item.date}</div>
              )}

              <div className="popular-actions">
                <div className="action-icon white">
                  <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                </div>
              </div>

              <div className="popular-card-content">
                <h3 style={{ color: '#fff' }}>{item.title}</h3>
                <div className="popular-meta">
                  {item.category && item.category.toUpperCase() !== 'UNCATEGORIZED' && (
                    <span style={{ color: '#fff' }}>{item.category}</span>
                  )}
                  <span className="popular-comments" style={{ color: '#fff' }} title="Views">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    {item.views.toLocaleString()}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>


      {/* ── CATEGORY CIRCLES ── */}
      {categories.length > 0 && (
        <section className="categories-section">
          <h2 className="section-title centered">RECIPES</h2>
          <div className="categories-slider-container">
            <div className="category-viewport">
              <div className="category-circles">
                {categories.map((cat) => (
                  <Link to={`/recipe?category=${cat.slug}`} className="category-item" key={cat.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="circle-wrap">
                      <img src={cat.img} alt={cat.name} />
                    </div>
                    <span>{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── LATEST RECIPES + SIDEBAR ── */}
      <section className="latest-section">
        <div className="latest-content">
          {/* Recipes Column */}
          <div className="recipes-column">
            <h2 className="section-title left">Latest Recipes</h2>
            <div className="recipe-cards">
              {latestRecipes.map((recipe, i) => (
                <div className="recipe-card" key={recipe.id}>
                  <Link to={`/blog-details/${recipe.slug || recipe.id}`} className="recipe-img-wrap hover-card" style={{ display: 'block' }}>
                    <img src={recipe.image || ''} alt={recipe.title} />
                    <div className="hover-black-overlay" />
                    <div className="hover-badge-tag">{recipe.date}</div>
                    <div className="hover-action-tag">
                      <div className="action-icon white">
                        <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                      </div>
                    </div>
                  </Link>
                  <div className="recipe-info">
                    <h3 className="recipe-title" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.3,
                      height: '2.6em'
                    }}>{recipe.title}</h3>
                    <div className="recipe-meta-row">
                    {recipe.category && recipe.category.toUpperCase() !== 'UNCATEGORIZED' && (
                      <span className="recipe-category">{recipe.category}</span>
                    )}
                      <span className="recipe-comments">
                        <svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                        {recipe.comments}
                      </span>
                    </div>
                    <p className="recipe-excerpt" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.5,
                      height: '4.5em'
                    }}>{recipe.excerpt || 'Read more about this delicious recipe by clicking below.'}</p>
                    <Link to={`/blog-details/${recipe.slug || recipe.id}`} className="read-more">READ MORE</Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="sidebar">
            {/* Author Card */}
            <div className="sidebar-widget author-card">
              <div className="author-avatar">
                <img src={authorAvatar} alt="Author" style={{ border: '4px solid #306046', borderRadius: '50%', padding: '4px', objectFit: 'cover' }} />
              </div>
              <h3 className="author-name">Hi, I am Loganayaki Thamilselvan!</h3>
              <p className="author-bio">Food stylist &amp; photographer. Loves nature and healthy food, and good coffee. Don't hesitate to come for say a small "hello!"</p>
              <div className="author-socials">
                <a href="#fb" aria-label="Facebook"><svg viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg></a>
                <a href="#tw" aria-label="Twitter"><svg viewBox="0 0 24 24"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" /></svg></a>
                <a href="#ig" aria-label="Instagram"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg></a>
                <a href="#pi" aria-label="Pinterest"><svg viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" /></svg></a>
                <a href="#yt" aria-label="YouTube"><svg viewBox="0 0 24 24"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" /><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="red" /></svg></a>
              </div>
              <Link to="/about" className="learn-more-btn">LEARN MORE</Link>
            </div>

            {/* Cookbook Banner */}
            <div className="sidebar-widget cookbook-banner">
              <img src={cookbookBanner} alt="Indian Cookbook" />
            </div>

            {/* Vegetarian Recipes Widget */}
            {vegRecipes.length > 0 && (
              <div className="sidebar-widget">
                <h4 className="widget-title">Vegetarian Recipes</h4>
                <div className="list-widget">
                  {vegRecipes.map((r, i) => (
                    <Link to={`/blog-details/${r.slug || r.id}`} className="list-widget-item" key={r.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div className="list-img"><img src={r.image || ''} alt={r.title} /></div>
                      <div className="list-info">
                        <h4>{r.title.split(/[-–]/)[0].trim()}</h4>
                        <p>{r.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Non-Vegetarian Recipes Widget */}
            {nonVegRecipes.length > 0 && (
              <div className="sidebar-widget">
                <h4 className="widget-title">Non-Vegetarian Recipes</h4>
                <div className="list-widget">
                  {nonVegRecipes.map((r, i) => (
                    <Link to={`/blog-details/${r.slug || r.id}`} className="list-widget-item" key={r.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div className="list-img"><img src={r.image || ''} alt={r.title} /></div>
                      <div className="list-info">
                        <h4>{r.title.split(/[-–]/)[0].trim()}</h4>
                        <p>{r.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Newsletter Widget */}
            <div className="sidebar-widget sidebar-newsletter">
              <h3>
                Never Miss A Recipe!
                <svg className="sidebar-title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </h3>
              <p>Join thousands of Famous Indian Recipes subscribers<br />and get our best recipes delivered<br />each week!</p>
              <form onSubmit={e => {
                e.preventDefault();
                setIsSubscribed(true);
              }}>
                <input type="email" placeholder="Your Email Address" required />
                <button type="submit">SUBSCRIBE</button>
                <div className="newsletter-terms">
                  <input type="checkbox" id="terms-chk" required />
                  <label htmlFor="terms-chk">I have read and agree to the <a href="#terms">terms &amp; conditions</a>.</label>
                </div>
              </form>
              {isSubscribed && (
                <div className="subscribe-success">
                  Thank you, your sign-up request was successful! Please check your email inbox to confirm.
                </div>
              )}
            </div>

            {/* Featured Recipes Widget */}
            {featuredRecipes.length > 0 && (
              <div className="sidebar-widget">
                <h4 className="widget-title">Featured Recipes</h4>
                <div className="featured-list">
                  {featuredRecipes.map((r, i) => (
                    <Link to={`/blog-details/${r.slug || r.id}`} className="featured-list-item" key={r.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div className="featured-bg" style={{ backgroundImage: `url(${r.image || ''})` }}></div>
                      <div className="featured-content-wrap">
                        <div className="featured-num">{i + 1}.</div>
                        <div className="featured-info">
                          <h4>{r.title.split(/[-–]/)[0].trim()}</h4>
                          <p>{r.date}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>


      {/* ── Featured Recipes ── */}
      {seasonCards.length > 0 && (
        <section className="season-section">
          <h2 className="section-title">Featured Recipes</h2>
          <div className="season-grid">
            {seasonCards.map((card, i) => (
              <Link to={`/blog-details/${card.slug || card.id}`} className="season-card hover-card" key={card.id} style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>
                <img src={card.image || ''} alt={card.title} />
                <div className="hover-black-overlay" />
                <div className="hover-badge-tag">{card.date}</div>
                <div className="hover-action-tag">
                  <div className="action-icon white">
                    <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                  </div>
                </div>
                <div className="season-card-body">
                  <h3 style={{ color: 'inherit' }} dangerouslySetInnerHTML={{ __html: card.title }} />
                  <div className="card-meta">
                    {card.category && card.category.toUpperCase() !== 'UNCATEGORIZED' && (
                      <span className="card-category">{card.category}</span>
                    )}
                    <span className="card-comments">
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                      {card.comments}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── MAKE THIS TODAY ── */}
      {makeToday && (
        <section className="make-today-section">
          <h2 className="section-title centered">Make This Today!</h2>
          <Link to={`/blog-details/${makeToday.slug || makeToday.id}`} className="make-today-wrapper" style={{ display: 'block', textDecoration: 'none' }}>
            <img src={makeToday.image || ''} alt={makeToday.title} className="make-today-img" />
            <div className="make-today-overlay" />

            <div className="make-today-content">
              <h3 style={{ color: '#fff' }}>{makeToday.title}</h3>
              <div className="make-today-meta">
                {makeToday.category && makeToday.category.toUpperCase() !== 'UNCATEGORIZED' && (
                  <span className="category" style={{ color: '#fff' }}>{makeToday.category}</span>
                )}
                <span className="comments" style={{ color: '#fff' }}>
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                  {makeToday.comments}
                </span>
              </div>
            </div>
          </Link>
        </section>
      )}

    </main>
  );
};

export default HomePage;
